import { usePermissions } from '@/hooks/permissions/usePermissions';

export type OrgRole = 'owner' | 'admin' | 'manager' | 'rep';

export interface OrgRoleResult {
  /** Papel NA ORG CORRENTE. `null` enquanto carrega ou sem membership. */
  role: OrgRole | null;
  /** Administra ESTA org: `admin` aqui, ou super admin da plataforma. */
  isOrgAdmin: boolean;
  /** Super administrador da plataforma (cross-org por natureza). */
  isSuperAdmin: boolean;
  loading: boolean;
}

/**
 * Papel do usuário NA ORGANIZAÇÃO CORRENTE.
 *
 * ── O que isto substitui, e por quê ──
 * `useIsAdmin` consultava `memberships` SEM `org_id`: ser admin em qualquer organização ligava
 * `isAdmin` em TODAS. Medido em 31/08: **92 pares (pessoa, org) de 34 pessoas** recebiam UI de
 * administrador em organizações onde não são admin — incluindo os guards de `/settings`,
 * `/contratos` e da integração ZapSign. Era o único eixo do front que não era escopado por org.
 *
 * ── Por que ler de `usePermissions` e não fazer query nova ──
 * `my_effective_access(org)` já devolve `role` e `is_super_admin`, já é escopado por construção
 * (`where m.org_id = p_org_id`), já está cacheado com `staleTime: Infinity` e já é purgado pelo
 * `perms_version`. A Sidebar monta esse hook em toda sessão, então isto não custa request nenhuma.
 *
 * ── `owner` NÃO é admin, de propósito ──
 * Medido: 53 owners não veem UI de admin hoje e 5 veem — estes 5 apenas porque são admin em
 * OUTRA org e o bug cross-org os resgatava. Incluir `owner` aqui alargaria para 53 pessoas e
 * contradiria o próprio produto, que trata owner como somente-leitura em `useCanEdit` (17
 * arquivos) e mapeou `owner -> "Somente leitura"` (0 chaves de ação) no backfill de grupos.
 * Consequência aceita: os 5 perdem, nesta org, uma UI que só tinham por bug — e seguem admin nas
 * organizações onde de fato são.
 */
export function useOrgRole(): OrgRoleResult {
  const { role, isSuperAdmin, loading } = usePermissions();

  // Sem fallback para `useOrganization` de propósito. Ele existiria só para a janela de
  // carregamento, mas fecharia um ciclo de import (auth -> useOrgRole -> OrganizationContext ->
  // useAuthManager -> useAppInitialization -> useGlobalLoading -> auth). E é desnecessário:
  // quem consome tem de respeitar `loading` antes de decidir — `ProtectedRoute` já faz isso,
  // mostrando "Verificando permissões…" em vez de negar.
  const efetivo = (role ?? null) as OrgRole | null;

  return {
    role: efetivo,
    isOrgAdmin: isSuperAdmin || efetivo === 'admin',
    isSuperAdmin,
    loading,
  };
}

/** Só a plataforma. Separado porque a pergunta é outra e confundir as duas é o bug de origem. */
export function useIsSuperAdmin(): { isSuperAdmin: boolean; loading: boolean } {
  const { isSuperAdmin, loading } = usePermissions();
  return { isSuperAdmin, loading };
}
